import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Plus, Pencil, Trash2, Users, ExternalLink, CalendarPlus, Archive, CalendarDays, Filter, RotateCcw, AlertTriangle, CheckCircle } from 'lucide-react';
import { DIVISION_NAMES, getJobsForDivision, OTHER_JOB } from '@/constants/divisions';
import JobWorkCard from '@/components/JobWorkCard';
import JobCardSteps from '@/components/JobCardSteps';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { format, parseISO, startOfWeek, endOfWeek, addWeeks, subWeeks, isWithinInterval } from 'date-fns';
import { istNow, istTodayKey, isThisWeekIST } from '@/utils/istClock';
import { calcDueDate, durationError, isOtherDuration, formatDayMonYear } from '@/utils/duration';
import { useAuth } from '@/contexts/AuthContext';
import { useTasks } from '@/contexts/TaskContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AccountDetail = () => {
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
  const toggleTaskSelection = (taskId) => {
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId); else next.add(taskId);
      return next;
    });
  };
  const handleBulkArchive = async () => {
    if (!window.confirm(`Archive ${selectedTaskIds.size} selected task(s)?`)) return;
    try {
      await Promise.all([...selectedTaskIds].map(id =>
        axios.put(`${API}/tasks/${id}`, {
          archived: true,
          archivedGlobally: true,
          archivedDate: new Date().toISOString()
        })
      ));
      toast.success(`${selectedTaskIds.size} task(s) archived`);
      setSelectedTaskIds(new Set());
      refreshTasks();
      fetchData();
    } catch (error) {
      console.error('Error bulk archiving:', error);
      toast.error('Failed to archive some tasks');
    }
  };
  const handleBulkDelete = async () => {
    const idsToDelete = [...selectedTaskIds];
    if (!window.confirm(`Delete ${idsToDelete.length} selected task(s)?`)) return;
    try {
      await Promise.all(idsToDelete.map(id => axios.delete(`${API}/tasks/${id}`)));
      setSelectedTaskIds(new Set());
      refreshTasks();
      fetchData();
      toast.success(`${idsToDelete.length} task(s) deleted`, {
        description: 'You have 20 minutes to undo this.',
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              await Promise.all(idsToDelete.map(id => axios.post(`${API}/tasks/${id}/restore`)));
              toast.success('Tasks restored');
              refreshTasks();
              fetchData();
            } catch {
              toast.error('Failed to restore some tasks');
            }
          }
        },
        duration: 15000
      });
    } catch (error) {
      console.error('Error bulk deleting:', error);
      toast.error('Failed to delete some tasks');
    }
  };
  const { accountId } = useParams();
  const { user } = useAuth();
  // Keep account-specific fetchData() but re-sync the global store after
  // mutations so other views stay consistent.
  const { refreshTasks } = useTasks();
  const [account, setAccount] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekFilter, setWeekFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [extensionDialogOpen, setExtensionDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedTaskForExtension, setSelectedTaskForExtension] = useState(null);
  // Date filter and archive states
  const [dateFilter, setDateFilter] = useState('all');
  const [specificDate, setSpecificDate] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const specificMonth = selectedMonth && selectedYear ? `${selectedYear}-${selectedMonth}` : '';
  const [showArchived, setShowArchived] = useState(false);
  const [raciFilter, setRaciFilter] = useState('all'); // all, responsible, accountable, consulted, informed
  const [memberFilter, setMemberFilter] = useState('all'); // all or a specific member id
  const [extensionData, setExtensionData] = useState({
    newDueDate: '',
    extensionReason: ''
  });
  const [formData, setFormData] = useState({
    title: '',
    deliverable: '',
    job: '',
    week: '',
    priority: 'med',
    status: 'pending',
    dueDate: '',
    responsible: [],
    accountable: [],
    consulted: [],
    informed: [],
    instructions: '',
    links: '',
    outcome: '',
    assignedBy: '',
    startDate: '',
    meetingDate: ''
  });

  useEffect(() => {
    if (accountId) {
      fetchData();
    }
  }, [accountId]);

  const fetchData = async () => {
    try {
      const [accountRes, tasksRes, membersRes] = await Promise.all([
        axios.get(`${API}/accounts/${accountId}`),
        axios.get(`${API}/tasks?accountId=${accountId}&include_archived=true`),
        axios.get(`${API}/team-members`)
      ]);
      setAccount(accountRes.data);
      setTasks(tasksRes.data);
      setTeamMembers(membersRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load account details');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isOtherDuration(formData) && (!formData.durationDays || durationError(formData.durationDays))) {
      toast.error(durationError(formData.durationDays) || 'Enter the number of days');
      return;
    }
    try {
      const taskData = {
        ...formData,
        accountId: accountId,
        links: formData.links ? formData.links.split(',').map(l => l.trim()) : [],
        durationDays: isOtherDuration(formData) ? Number(formData.durationDays) : 0,  // CR-16 (0 = no duration)
        durationMode: undefined,
      };

      if (editingTask) {
        await axios.put(`${API}/tasks/${editingTask.id}`, taskData);
        toast.success('Task updated', { description: 'Synced across all views' });
      } else {
        await axios.post(`${API}/tasks`, taskData);
        toast.success('Task created', { description: 'Synced to team members' });
      }

      setDialogOpen(false);
      setEditingTask(null);
      resetForm();
      fetchData();
      refreshTasks();
    } catch (error) {
      console.error('Error saving task:', error);
      toast.error('Failed to save task');
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title || '',
      deliverable: task.deliverable || '',
      job: task.job || '',
      week: task.week || 'Week 1',
      priority: task.priority || 'med',
      status: task.status || 'pending',
      dueDate: task.dueDate ? task.dueDate.split('T')[0] : '',
      responsible: task.responsible || [],
      accountable: task.accountable || [],
      consulted: task.consulted || [],
      informed: task.informed || [],
      instructions: task.instructions || '',
      links: task.links ? task.links.join(', ') : '',
      outcome: task.outcome || '',
      assignedBy: task.assignedBy || '',
      startDate: task.startDate ? task.startDate.split('T')[0] : '',
      durationDays: task.durationDays > 0 ? String(task.durationDays) : '',
      durationMode: task.durationDays > 0 ? 'other' : 'specific',
      meetingDate: task.meetingDate ? task.meetingDate.split('T')[0] : ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      toast.success('Task deleted');
      fetchData();
      refreshTasks();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      deliverable: '',
      job: '',
      week: '',
      priority: 'med',
      status: 'pending',
      dueDate: '',
      responsible: [],
      accountable: [],
      consulted: [],
      informed: [],
      instructions: '',
      links: '',
      outcome: '',
      assignedBy: '',
      startDate: '',
      meetingDate: ''
    });
  };

  const toggleMemberInRole = (role, memberId) => {
    setFormData(prev => {
      const currentList = prev[role] || [];
      if (currentList.includes(memberId)) {
        return { ...prev, [role]: currentList.filter(id => id !== memberId) };
      } else {
        return { ...prev, [role]: [...currentList, memberId] };
      }
    });
  };

  // Extension handlers for overdue tasks
  const openExtensionDialog = (task) => {
    setSelectedTaskForExtension(task);
    setExtensionData({ newDueDate: '', extensionReason: '' });
    setExtensionDialogOpen(true);
  };

  const handleExtensionSubmit = async () => {
    if (!extensionData.newDueDate) {
      toast.error('Please select a new due date');
      return;
    }
    if (!extensionData.extensionReason) {
      toast.error('Please provide a reason for the extension');
      return;
    }

    try {
      await axios.put(`${API}/tasks/${selectedTaskForExtension.id}`, {
        originalDueDate: selectedTaskForExtension.originalDueDate || selectedTaskForExtension.dueDate,
        dueDate: extensionData.newDueDate,
        extensionReason: extensionData.extensionReason,
        extendedBy: user?.id
      });
      toast.success('Due date extended');
      setExtensionDialogOpen(false);
      setSelectedTaskForExtension(null);
      fetchData();
      refreshTasks();
    } catch (error) {
      console.error('Error extending due date:', error);
      toast.error('Failed to extend due date');
    }
  };

  // Archive handlers (GLOBAL - Admin from Account Detail page)
  const handleArchive = async (task) => {
    try {
      await axios.put(`${API}/tasks/${task.id}`, {
        archived: true,
        archivedGlobally: true,
        archivedDate: new Date().toISOString()
      });
      toast.success('Task archived globally');
      fetchData();
      refreshTasks();
    } catch (error) {
      console.error('Error archiving task:', error);
      toast.error('Failed to archive task');
    }
  };

  const handleUnarchive = async (task) => {
    try {
      await axios.put(`${API}/tasks/${task.id}`, {
        archived: false,
        archivedGlobally: false,
        archivedDate: null
      });
      toast.success('Task restored globally');
      fetchData();
      refreshTasks();
    } catch (error) {
      console.error('Error restoring task:', error);
      toast.error('Failed to restore task');
    }
  };

  // Filter tasks by date
  const filterTasksByDate = (taskList) => {
    if (dateFilter === 'all' || dateFilter === 'done') return taskList;
    
    return taskList.filter(task => {
      if (!task.dueDate) return dateFilter === 'no-date';
      
      try {
        const dueDate = parseISO(task.dueDate);
        const now = istNow();
        
        switch (dateFilter) {
          case 'today': {
            // CR-15 (updated): Start Date is today (IST); a task without a Start Date uses its Due Date
            return format(task.startDate ? parseISO(task.startDate) : dueDate, 'yyyy-MM-dd') === istTodayKey();
          }
          case 'this-week':
            return isThisWeekIST(dueDate, { weekStartsOn: 1 });
          case 'last-week': {
            const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
            const lastWeekEnd = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
            return isWithinInterval(dueDate, { start: lastWeekStart, end: lastWeekEnd });
          }
          case 'next-week': {
            const nextWeekStart = startOfWeek(addWeeks(now, 1), { weekStartsOn: 1 });
            const nextWeekEnd = endOfWeek(addWeeks(now, 1), { weekStartsOn: 1 });
            return isWithinInterval(dueDate, { start: nextWeekStart, end: nextWeekEnd });
          }
          case 'overdue':
            return task.isLagging;
          case 'specific': {
            if (!specificDate) return true;
            const pickedDay = parseISO(specificDate);
            const effectiveStartSpecific = task.startDate || task.assignedDate;
            if (effectiveStartSpecific) {
              const startDay = new Date(parseISO(effectiveStartSpecific)); startDay.setHours(0,0,0,0);
              const dueDay = new Date(dueDate); dueDay.setHours(0,0,0,0);
              return startDay <= pickedDay && pickedDay <= dueDay;
            }
            return format(dueDate, 'yyyy-MM-dd') === specificDate;
          }
          case 'specific-month':
            if (!specificMonth) return true;
            return format(dueDate, 'yyyy-MM') === specificMonth;
          case 'no-date':
            return false;
          default:
            return true;
        }
      } catch {
        return false;
      }
    });
  };

  // Helper to filter tasks by RACI role
  const filterByRaci = (taskList) => {
    if (raciFilter === 'all') return taskList;
    return taskList.filter(task => {
      switch (raciFilter) {
        case 'responsible':
          return task.responsible?.length > 0;
        case 'accountable':
          return task.accountable?.length > 0;
        case 'consulted':
          return task.consulted?.length > 0;
        case 'informed':
          return task.informed?.length > 0;
        default:
          return true;
      }
    });
  };

  // Helper to filter tasks by a specific team member (any RACI role)
  const filterByMember = (taskList) => {
    if (memberFilter === 'all') return taskList;
    if (raciFilter !== 'all') {
      return taskList.filter(task => task[raciFilter]?.includes(memberFilter));
    }
    return taskList.filter(task =>
      task.responsible?.includes(memberFilter) ||
      task.accountable?.includes(memberFilter) ||
      task.consulted?.includes(memberFilter) ||
      task.informed?.includes(memberFilter)
    );
  };
  // Count stats (respecting RACI filter and member filter)
  const raciFilteredTasks = filterByMember(filterByRaci(tasks));
  const activeTasksCount = raciFilteredTasks.filter(t => !t.archived && !t.archivedGlobally && !['Done', 'done'].includes(t.status)).length;
  const doneTasksCount = raciFilteredTasks.filter(t => !t.archived && !t.archivedGlobally && ['Done', 'done'].includes(t.status)).length;
  const archivedTasksCount = raciFilteredTasks.filter(t => t.archived || t.archivedGlobally).length;
  const todayTasksCount = raciFilteredTasks.filter(t => {
    if (!t.dueDate || t.archived || t.archivedGlobally) return false;
    try {
      const due = parseISO(t.dueDate);
      return format(t.startDate ? parseISO(t.startDate) : due, 'yyyy-MM-dd') === istTodayKey(); // CR-15 (updated): start date today (IST), else due today
    } catch {
      return false;
    }
  }).length;
  const overdueTasksCount = raciFilteredTasks.filter(t => !t.archived && !t.archivedGlobally && t.isLagging).length;
  const thisWeekTasksCount = raciFilteredTasks.filter(t => {
    if (!t.dueDate || t.archived || t.archivedGlobally) return false;
    try {
      return isThisWeekIST(parseISO(t.dueDate), { weekStartsOn: 1 });
    } catch {
      return false;
    }
  }).length;
  const lastWeekTasksCount = raciFilteredTasks.filter(t => {
    if (!t.dueDate || t.archived || t.archivedGlobally) return false;
    try {
      const dueDate = parseISO(t.dueDate);
      const now = istNow();
      const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
      const lastWeekEnd = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
      return isWithinInterval(dueDate, { start: lastWeekStart, end: lastWeekEnd });
    } catch {
      return false;
    }
  }).length;
  const nextWeekTasksCount = raciFilteredTasks.filter(t => {
    if (!t.dueDate || t.archived || t.archivedGlobally) return false;
    try {
      const dueDate = parseISO(t.dueDate);
      const now = istNow();
      const nextWeekStart = startOfWeek(addWeeks(now, 1), { weekStartsOn: 1 });
      const nextWeekEnd = endOfWeek(addWeeks(now, 1), { weekStartsOn: 1 });
      return isWithinInterval(dueDate, { start: nextWeekStart, end: nextWeekEnd });
    } catch {
      return false;
    }
  }).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  // Get filtered tasks (by week, date filter, archive status, and RACI role)
  const getFilteredTasks = () => {
    let filtered = tasks;
    
    // Filter by week
    if (weekFilter !== 'all') {
      filtered = filtered.filter(t => t.week === weekFilter);
    }
    
    // Filter by archive status
    if (!showArchived) {
      filtered = filtered.filter(t => !t.archived && !t.archivedGlobally);
      if (dateFilter === 'done') {
        filtered = filtered.filter(t => ['Done', 'done'].includes(t.status));
      } else {
        filtered = filtered.filter(t => !['Done', 'done'].includes(t.status));
      }
    } else {
      filtered = filtered.filter(t => t.archived || t.archivedGlobally);
    }
    
    // Filter by RACI role
    if (raciFilter !== 'all') {
      filtered = filtered.filter(task => {
        switch (raciFilter) {
          case 'responsible':
            return task.responsible?.length > 0;
          case 'accountable':
            return task.accountable?.length > 0;
          case 'consulted':
            return task.consulted?.length > 0;
          case 'informed':
            return task.informed?.length > 0;
          default:
            return true;
        }
      });
    }
    
    // Filter by team member
    if (memberFilter !== 'all') {
      if (raciFilter !== 'all') {
        filtered = filtered.filter(task => task[raciFilter]?.includes(memberFilter));
      } else {
        filtered = filtered.filter(task =>
          task.responsible?.includes(memberFilter) ||
          task.accountable?.includes(memberFilter) ||
          task.consulted?.includes(memberFilter) ||
          task.informed?.includes(memberFilter)
        );
      }
    }
    
    // Filter by date
    filtered = filterTasksByDate(filtered);
    
    // Sort: today first, then each later date in order; past due dates after that; no due date last.
    // Within the same date: priority (High, Medium, Low), then title A-Z.
    const sortTodayKey = istTodayKey();
    const sortDayKey = (d) => { try { return d ? format(parseISO(d), 'yyyy-MM-dd') : ''; } catch { return ''; } };
    const sortGroup = (k) => (!k ? 2 : k >= sortTodayKey ? 0 : 1);
    const sortPriority = { high: 0, med: 1, medium: 1, low: 2 };
    return [...filtered].sort((a, b) => {
      const ak = sortDayKey(a.dueDate), bk = sortDayKey(b.dueDate);
      const g = sortGroup(ak) - sortGroup(bk);
      if (g !== 0) return g;
      if (ak !== bk) return ak < bk ? -1 : 1;
      const p = (sortPriority[a.priority] ?? 3) - (sortPriority[b.priority] ?? 3);
      if (p !== 0) return p;
      return (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' });
    });
  };

  const filteredTasks = getFilteredTasks();

  const groupedByDeliverable = filteredTasks.reduce((acc, task) => {
    const deliverable = task.deliverable || 'Uncategorized';
    if (!acc[deliverable]) acc[deliverable] = [];
    acc[deliverable].push(task);
    return acc;
  }, {});

  const getMemberById = (id) => teamMembers.find(m => m.id === id);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="p-8 space-y-6"
      data-testid="account-detail-page"
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div 
            className="h-12 w-12 sm:h-16 sm:w-16 flex-shrink-0 rounded-lg flex items-center justify-center text-white font-bold text-lg sm:text-2xl"
            style={{ backgroundColor: account?.color }}
          >
            {account?.name.charAt(0)}
          </div>
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold tracking-tight truncate">{account?.name}</h1>
            <p className="text-muted-foreground mt-1">{account?.description || 'Account tasks and deliverables'}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <a
            href={`/report/${accountId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors text-sm font-medium"
            data-testid="client-preview-btn"
          >
            <ExternalLink className="h-4 w-4" />
            Client Preview
          </a>
          <Select value={weekFilter} onValueChange={setWeekFilter}>
            <SelectTrigger className="w-40 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All Weeks</SelectItem>
              <SelectItem value="Week 1">Week 1</SelectItem>
              <SelectItem value="Week 2">Week 2</SelectItem>
              <SelectItem value="Week 3">Week 3</SelectItem>
              <SelectItem value="Week 4">Week 4</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-primary hover:bg-primary/90"
                onClick={() => { resetForm(); setEditingTask(null); }}
                data-testid="add-task-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Task
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold">
                  {editingTask ? 'Edit Task' : 'Create New Task'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label className="text-sm font-semibold">Division *</Label>
                    <Select
                      value={formData.deliverable}
                      onValueChange={(value) => setFormData({ ...formData, deliverable: value, job: '', title: '' })}
                    >
                      <SelectTrigger className="mt-1.5 bg-white" data-testid="division-select">
                        <SelectValue placeholder="Select a division" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {formData.deliverable && !DIVISION_NAMES.includes(formData.deliverable) && (
                          <SelectItem value={formData.deliverable}>{formData.deliverable} (old value)</SelectItem>
                        )}
                        {DIVISION_NAMES.map((name) => (
                          <SelectItem key={name} value={name}>{name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="mt-4">
                      <Label className="text-sm font-semibold">Job (work card)</Label>
                      <Select
                        value={formData.job}
                        onValueChange={(value) => setFormData({ ...formData, job: value, title: value === OTHER_JOB ? '' : value })}
                        disabled={!formData.deliverable}
                      >
                        <SelectTrigger className="mt-1.5 bg-white" data-testid="job-select">
                          <SelectValue placeholder={formData.deliverable ? 'Select a job' : 'Select a division first'} />
                        </SelectTrigger>
                        <SelectContent className="bg-white max-h-80">
                          {formData.job && !getJobsForDivision(formData.deliverable).includes(formData.job) && (
                            <SelectItem value={formData.job}>{formData.job}</SelectItem>
                          )}
                          {getJobsForDivision(formData.deliverable).map((job) => (
                            <SelectItem key={job} value={job}>{job}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <JobCardSteps division={formData.deliverable} job={formData.job} defaultOpen className="mt-3" />
                    </div>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-sm font-semibold">Task Title *</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      readOnly={!!formData.job && formData.job !== OTHER_JOB}
                      className={`mt-1.5 ${(formData.job && formData.job !== OTHER_JOB) ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white'}`}
                      placeholder={(formData.job && formData.job !== OTHER_JOB) ? 'Filled from the job you selected' : 'Specific task description'}
                    />
                  </div>
                  {false && (
                  <div>
                    <Label className="text-sm font-semibold">Week</Label>
                    <Select value={formData.week} onValueChange={(value) => setFormData({ ...formData, week: value })}>
                      <SelectTrigger className="mt-1.5 bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="Week 1">Week 1</SelectItem>
                        <SelectItem value="Week 2">Week 2</SelectItem>
                        <SelectItem value="Week 3">Week 3</SelectItem>
                        <SelectItem value="Week 4">Week 4</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  )}
                  <div>
                    <Label className="text-sm font-semibold">Duration</Label>
                    <Select
                      value={isOtherDuration(formData) ? 'other' : 'specific'}
                      onValueChange={(value) => setFormData(value === 'other'
                        ? { ...formData, durationMode: 'other', dueDate: calcDueDate(formData.startDate, formData.durationDays) || formData.dueDate }
                        : { ...formData, durationMode: 'specific', durationDays: '' })}
                    >
                      <SelectTrigger className="mt-1.5 bg-white" data-testid="duration-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="specific">Specific Date</SelectItem>
                        <SelectItem value="other">Other (number of days)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {isOtherDuration(formData) && (
                    <div>
                      <Label className="text-sm font-semibold">Number of Days</Label>
                      <Input
                        type="number"
                        min="1"
                        max="365"
                        step="1"
                        data-testid="duration-days-input"
                        value={formData.durationDays || ''}
                        onChange={(e) => setFormData({ ...formData, durationMode: 'other', durationDays: e.target.value, dueDate: calcDueDate(formData.startDate, e.target.value) || formData.dueDate })}
                        className="mt-1.5 bg-white"
                        placeholder="e.g. 3"
                      />
                      {durationError(formData.durationDays) && (
                        <p className="text-xs text-red-600 mt-1">{durationError(formData.durationDays)}</p>
                      )}
                      {!formData.startDate && (
                        <p className="text-xs text-amber-700 mt-1">Pick the Start Date and the Due Date is worked out for you (the Start Date is Day 1)</p>
                      )}
                    </div>
                  )}
                  <div>
                    <Label className="text-sm font-semibold">Due Date</Label>
                    <Input
                      type="date"
                      data-testid="due-date-input"
                      value={formData.dueDate}
                      onChange={(e) => setFormData({ ...formData, dueDate: e.target.value, durationMode: 'specific', durationDays: '' })}
                      className="mt-1.5 bg-white"
                    />
                    {isOtherDuration(formData) && formData.dueDate && !durationError(formData.durationDays) && formData.durationDays && (
                      <p className="text-xs text-gray-600 mt-1" data-testid="due-date-note">{formatDayMonYear(formData.dueDate)} · {formData.durationDays}-day task</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-sm font-semibold">Assigned By</Label>
                    <Select value={formData.assignedBy} onValueChange={(value) => setFormData({ ...formData, assignedBy: value })}>
                      <SelectTrigger className="mt-1.5 bg-white">
                        <SelectValue placeholder="Select who assigned this" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {teamMembers.map((m) => (
                          <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold">Start Date</Label>
                    <Input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value, ...(isOtherDuration(formData) && calcDueDate(e.target.value, formData.durationDays) ? { dueDate: calcDueDate(e.target.value, formData.durationDays) } : {}) })}
                      className="mt-1.5 bg-white"
                    />
                  </div>
                  {false && (
                  <div>
                    <Label className="text-sm font-semibold">Relevant Meeting Date</Label>
                    <Input
                      type="date"
                      value={formData.meetingDate}
                      onChange={(e) => setFormData({ ...formData, meetingDate: e.target.value })}
                      className="mt-1.5 bg-white"
                    />
                  </div>
                  )}
                  <div>
                    <Label className="text-sm font-semibold">Priority</Label>
                    <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                      <SelectTrigger className="mt-1.5 bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="high">🔴 High</SelectItem>
                        <SelectItem value="med">🟡 Medium</SelectItem>
                        <SelectItem value="low">🟢 Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold">Status</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                      <SelectTrigger className="mt-1.5 bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="Yet to Start">Yet to Start</SelectItem>
                        <SelectItem value="In Progress">In Progress</SelectItem>
                        <SelectItem value="Done">Done</SelectItem>
                        <SelectItem value="On Hold">On Hold</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* RACI Matrix */}
                <div className="border-t pt-4">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    RACI Matrix - Assign Team Members
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {['responsible', 'accountable', 'consulted', 'informed'].map((role) => (
                      <div key={role} className="border rounded-lg p-4 bg-gray-50">
                        <Label className="text-sm font-bold capitalize mb-3 block">
                          {role.charAt(0).toUpperCase()} - {role === 'responsible' && 'Does the work'}
                          {role === 'accountable' && 'Ultimately answerable'}
                          {role === 'consulted' && 'Provides input'}
                          {role === 'informed' && 'Kept updated'}
                        </Label>
                        <div className="space-y-2">
                          {teamMembers.map((member) => (
                            <label key={member.id} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData[role]?.includes(member.id)}
                                onChange={() => toggleMemberInRole(role, member.id)}
                                className="h-4 w-4 rounded border-gray-300"
                              />
                              <span className="text-sm">{member.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-semibold">Instructions / SOP</Label>
                  <Textarea
                    value={formData.instructions}
                    onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                    rows={3}
                    className="mt-1.5 bg-white"
                    placeholder="Add detailed instructions"
                  />
                </div>

                <div>
                  <Label className="text-sm font-semibold">Outcome (Client-facing result)</Label>
                  <Textarea
                    value={formData.outcome}
                    onChange={(e) => setFormData({ ...formData, outcome: e.target.value })}
                    rows={2}
                    className="mt-1.5 bg-white"
                    placeholder="Describe the result/outcome for client reports (e.g., 'Increased engagement by 25%')"
                  />
                </div>

                <div>
                  <Label className="text-sm font-semibold">Links (comma separated)</Label>
                  <Input
                    value={formData.links}
                    onChange={(e) => setFormData({ ...formData, links: e.target.value })}
                    placeholder="https://example.com, https://another.com"
                    className="mt-1.5 bg-white"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-primary hover:bg-primary/90">
                    {editingTask ? 'Update Task' : 'Create Task'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Date Filter and Archive Controls */}
      <Card className="p-4 bg-white border-border/60">
        <div className="flex flex-wrap items-center gap-4">
          {/* Date Filter Buttons */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">Filter:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={dateFilter === 'all' ? 'default' : 'outline'}
              onClick={() => { setDateFilter('all'); setSpecificDate(''); }}
              data-testid="filter-all-btn"
            >
              All ({activeTasksCount})
            </Button>
            <Button
              size="sm"
              variant={dateFilter === 'today' ? 'default' : 'outline'}
              onClick={() => { setDateFilter('today'); setSpecificDate(''); }}
              data-testid="filter-today-btn"
            >
              Today ({todayTasksCount})
            </Button>
            <Button
              size="sm"
              variant={dateFilter === 'last-week' ? 'default' : 'outline'}
              onClick={() => { setDateFilter('last-week'); setSpecificDate(''); }}
              data-testid="filter-last-week-btn"
            >
              Last Week ({lastWeekTasksCount})
            </Button>
            <Button
              size="sm"
              variant={dateFilter === 'this-week' ? 'default' : 'outline'}
              onClick={() => { setDateFilter('this-week'); setSpecificDate(''); }}
              className={dateFilter === 'this-week' ? 'bg-blue-600 hover:bg-blue-700' : 'text-blue-600 border-blue-300'}
              data-testid="filter-this-week-btn"
            >
              This Week ({thisWeekTasksCount})
            </Button>
            <Button
              size="sm"
              variant={dateFilter === 'next-week' ? 'default' : 'outline'}
              onClick={() => { setDateFilter('next-week'); setSpecificDate(''); }}
              data-testid="filter-next-week-btn"
            >
              Next Week ({nextWeekTasksCount})
            </Button>
            <Button
              size="sm"
              variant={dateFilter === 'overdue' ? 'default' : 'outline'}
              onClick={() => { setDateFilter('overdue'); setSpecificDate(''); }}
              className={dateFilter === 'overdue' ? 'bg-red-600 hover:bg-red-700' : 'text-red-600 border-red-300'}
              data-testid="filter-overdue-btn"
            >
              <AlertTriangle className="h-3 w-3 mr-1" />
              Overdue ({overdueTasksCount})
            </Button>
          </div>

          {/* Date Picker */}
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
            <Input
              type="date"
              value={specificDate}
              onChange={(e) => {
                setSpecificDate(e.target.value);
                setDateFilter('specific');
              }}
              className="w-40 h-9 bg-white"
              data-testid="date-picker"
            />
            {specificDate && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setSpecificDate(''); setDateFilter('all'); }}
                className="h-9 px-2"
              >
                Clear
              </Button>
            )}
          </div>

          {/* Month Picker */}
          <div className="flex items-center gap-2">
            <Select
              value={selectedMonth}
              onValueChange={(v) => { setSelectedMonth(v); setDateFilter('specific-month'); }}
            >
              <SelectTrigger className="w-28 h-9 bg-white" data-testid="month-picker">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {['01','02','03','04','05','06','07','08','09','10','11','12'].map((m, idx) => (
                  <SelectItem key={m} value={m}>
                    {['January','February','March','April','May','June','July','August','September','October','November','December'][idx]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={selectedYear}
              onValueChange={(v) => { setSelectedYear(v); setDateFilter('specific-month'); }}
            >
              <SelectTrigger className="w-24 h-9 bg-white" data-testid="year-picker">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {[2025, 2026, 2027].map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(selectedMonth || selectedYear) && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setSelectedMonth(''); setSelectedYear(''); setDateFilter('all'); }}
                className="h-9 px-2"
              >
                Clear
              </Button>
            )}
          </div>
          {/* RACI Role Filter */}
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <Select value={raciFilter} onValueChange={setRaciFilter}>
              <SelectTrigger className="w-44 h-9 bg-white" data-testid="raci-filter">
                <SelectValue placeholder="RACI Role" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="responsible">
                  <span className="flex items-center gap-2">
                    <span className="px-1.5 py-0.5 bg-blue-500 text-white rounded text-xs font-bold">R</span>
                    Responsible
                  </span>
                </SelectItem>
                <SelectItem value="accountable">
                  <span className="flex items-center gap-2">
                    <span className="px-1.5 py-0.5 bg-purple-500 text-white rounded text-xs font-bold">A</span>
                    Accountable
                  </span>
                </SelectItem>
                <SelectItem value="consulted">
                  <span className="flex items-center gap-2">
                    <span className="px-1.5 py-0.5 bg-green-500 text-white rounded text-xs font-bold">C</span>
                    Consulted
                  </span>
                </SelectItem>
                <SelectItem value="informed">
                  <span className="flex items-center gap-2">
                    <span className="px-1.5 py-0.5 bg-gray-500 text-white rounded text-xs font-bold">I</span>
                    Informed
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Team Member Filter */}
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <Select value={memberFilter} onValueChange={setMemberFilter}>
              <SelectTrigger className="w-44 h-9 bg-white" data-testid="member-filter">
                <SelectValue placeholder="Team Member" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Members</SelectItem>
                {teamMembers.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Archive Toggle */}
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            {/* Bulk Archive Done Tasks */}
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                const doneCount = tasks.filter(t => t.status === 'Done' || t.status === 'done').filter(t => !t.archivedGlobally).length;
                if (doneCount === 0) {
                  toast.info('No completed tasks to archive');
                  return;
                }
                if (!window.confirm(`Archive ${doneCount} completed tasks?`)) return;
                try {
                  const res = await axios.post(`${API}/tasks/bulk-archive-done?accountId=${accountId}`);
                  toast.success(res.data.message);
                  fetchData();
                  refreshTasks();
                } catch (err) {
                  toast.error('Failed to archive tasks');
                }
              }}
              className="border-green-300 text-green-700 hover:bg-green-50"
              data-testid="bulk-archive-done-btn"
            >
              <Archive className="h-4 w-4 mr-1" />
              Archive Done Tasks
            </Button>
            <Button
              size="sm"
              variant={dateFilter === 'done' && !showArchived ? 'default' : 'outline'}
              onClick={() => { setDateFilter('done'); setShowArchived(false); }}
              className={dateFilter === 'done' && !showArchived ? 'bg-green-600 hover:bg-green-700' : 'text-green-700 border-green-300'}
              data-testid="filter-done-btn"
            >
              <CheckCircle className="h-4 w-4 mr-1" />
              Done ({doneTasksCount})
            </Button>
            <Button
              size="sm"
              variant={showArchived ? 'default' : 'outline'}
              onClick={() => setShowArchived(!showArchived)}
              className={showArchived ? 'bg-slate-700' : ''}
              data-testid="toggle-archived-btn"
            >
              <Archive className="h-4 w-4 mr-1" />
              {showArchived ? 'Showing Archived' : 'Show Archived'} ({archivedTasksCount})
            </Button>
          </div>
        </div>
      </Card>

      {/* Deliverables and Tasks */}
      <div className="space-y-6">
      {/* Bulk Action Bar */}
      {selectedTaskIds.size > 0 && (
        <Card className="p-4 bg-blue-50 border-blue-200 flex items-center justify-between mb-4">
          <span className="text-sm font-medium">{selectedTaskIds.size} task(s) selected</span>
          <div className="flex gap-2">
            {filteredTasks.some(t => !selectedTaskIds.has(t.id)) && (
              <Button
                size="sm"
                variant="outline"
                className="border-blue-400 text-blue-700"
                onClick={() => setSelectedTaskIds(new Set(filteredTasks.map(t => t.id)))}
                data-testid="select-all-btn"
              >
                Select All ({filteredTasks.length})
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              className="border-slate-400 text-slate-700"
              onClick={handleBulkArchive}
              data-testid="bulk-archive-btn"
            >
              Archive Selected
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={handleBulkDelete}
              data-testid="bulk-delete-btn"
            >
              Delete Selected
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelectedTaskIds(new Set())}
            >
              Clear
            </Button>
          </div>
        </Card>
      )}
        {Object.entries(groupedByDeliverable).map(([deliverable, deliverableTasks]) => (
          <Card key={deliverable} className="p-6 bg-white border-l-4" style={{ borderLeftColor: account?.color }}>
            <h2 className="text-2xl font-semibold mb-4">{deliverable}</h2>
            <div className="space-y-3">
              {deliverableTasks.map((task) => {
                const responsibleMembers = task.responsible?.map(getMemberById).filter(Boolean) || [];
                const accountableMembers = task.accountable?.map(getMemberById).filter(Boolean) || [];

                return (
                  <div
                    key={task.id}
                    className={`p-4 border rounded-lg transition-colors ${
                      task.isLagging ? 'bg-red-50 border-red-300' :
                      task.status === 'Done' ? 'bg-green-50 border-green-300' :
                      'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={selectedTaskIds.has(task.id)}
                        onChange={() => toggleTaskSelection(task.id)}
                        data-testid={`select-task-${task.id}`}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h3 className="font-semibold text-lg">{task.title}</h3>
                          {task.broadCategory && (
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              task.broadCategory === 'Social Media' ? 'bg-pink-100 text-pink-700' :
                              task.broadCategory === 'Video Content' ? 'bg-red-100 text-red-700' :
                              task.broadCategory === 'LinkedIn/Professional' ? 'bg-sky-100 text-sky-700' :
                              task.broadCategory === 'Ads & Paid' ? 'bg-amber-100 text-amber-700' :
                              task.broadCategory === 'Content Planning' ? 'bg-violet-100 text-violet-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {task.broadCategory}
                            </span>
                          )}
                          {task.week && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                              {task.week}
                            </span>
                          )}
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                            task.priority === 'high' ? 'bg-red-100 text-red-700' :
                            task.priority === 'med' ? 'bg-orange-100 text-orange-700' :
                            'bg-emerald-100 text-emerald-700'
                          }`}>
                            {task.priority === 'high' ? '🔴' : task.priority === 'med' ? '🟡' : '🟢'} {task.priority.toUpperCase()}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            task.status === 'Done' ? 'bg-green-100 text-green-700' :
                            task.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {task.status}
                          </span>
                          {/* Ideation Status for execution tasks */}
                          {task.ideationTaskId && task.ideationStatus === 'pending' && (
                            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded text-xs font-medium animate-pulse">
                              ⏳ Awaiting Ideation
                            </span>
                          )}
                          {task.isIdeationTask && (
                            <span className="px-2 py-0.5 bg-violet-100 text-violet-700 rounded text-xs font-medium">
                              📋 Ideation Task
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1 mb-3 text-xs text-gray-600">
                          {task.dueDate && (
                            <p className={task.isLagging ? 'text-red-700 font-semibold' : ''}>
                              <span className="text-gray-400">Due:</span> {format(new Date(task.dueDate), 'MMM dd')}
                            </p>
                          )}
                          {task.assignedBy && (
                            <p><span className="text-gray-400">By:</span> {getMemberById(task.assignedBy)?.name || 'Unknown'}</p>
                          )}
                          {task.assignedDate && (
                            <p><span className="text-gray-400">Assigned:</span> {format(new Date(task.assignedDate), 'MMM dd')}</p>
                          )}
                          {task.startDate && (
                            <p><span className="text-gray-400">Start:</span> {format(new Date(task.startDate), 'MMM dd')}{task.durationDays > 0 && <span className="text-gray-500"> ({task.durationDays}-day task)</span>}</p>
                          )}
                          {task.meetingDate && (
                            <p><span className="text-gray-400">Meeting:</span> {format(new Date(task.meetingDate), 'MMM dd')}</p>
                          )}
                          {task.completedDate && (
                            <p className="text-green-700 font-medium"><span className="text-gray-400">Done:</span> {format(new Date(task.completedDate), 'MMM dd')}</p>
                          )}
                        </div>
                        {/* RACI Display */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
                          {responsibleMembers.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-blue-700 mb-1">R - Responsible</p>
                              <div className="flex flex-wrap gap-1">
                                {responsibleMembers.map((m) => (
                                  <span key={m.id} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                                    {m.name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {accountableMembers.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-purple-700 mb-1">A - Accountable</p>
                              <div className="flex flex-wrap gap-1">
                                {accountableMembers.map((m) => (
                                  <span key={m.id} className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                                    {m.name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {(task.consulted?.length > 0) && (
                            <div>
                              <p className="text-xs font-semibold text-green-700 mb-1">C - Consulted</p>
                              <div className="flex flex-wrap gap-1">
                                {task.consulted.map((id) => {
                                  const m = getMemberById(id);
                                  return m ? (
                                    <span key={id} className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                                      {m.name}
                                    </span>
                                  ) : null;
                                })}
                              </div>
                            </div>
                          )}
                          {(task.informed?.length > 0) && (
                            <div>
                              <p className="text-xs font-semibold text-gray-700 mb-1">I - Informed</p>
                              <div className="flex flex-wrap gap-1">
                                {task.informed.map((id) => {
                                  const m = getMemberById(id);
                                  return m ? (
                                    <span key={id} className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs">
                                      {m.name}
                                    </span>
                                  ) : null;
                                })}
                              </div>
                            </div>
                          )}
                        </div>

                        <JobWorkCard task={task} className="mt-3" />
                        <JobCardSteps division={task.deliverable} job={task.job} className="mt-2" />
                        {task.instructions && (
                          <p className="text-sm text-gray-600 mt-3 p-2 bg-gray-100 rounded whitespace-pre-wrap">
                            {task.instructions}
                          </p>
                        )}
                        {task.outcome && (
                          <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                            <p className="text-[10px] font-semibold text-blue-700 uppercase mb-0.5">Outcome</p>
                            <p className="text-sm text-blue-900">{task.outcome}</p>
                          </div>
                        )}

                        {/* Ideation Reference - shown for execution tasks when ideation is complete */}
                        {task.ideationTaskId && task.ideationStatus === 'complete' && task.ideationReference && (
                          <div className="mt-3 p-3 bg-violet-50 border-2 border-violet-300 rounded-lg">
                            <p className="text-xs font-bold text-violet-700 mb-1 uppercase">📋 Ideation Reference:</p>
                            {task.ideationReference.note && (
                              <p className="text-sm text-violet-900">{task.ideationReference.note}</p>
                            )}
                            {task.ideationReference.links && task.ideationReference.links.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {task.ideationReference.links.map((link, idx) => (
                                  <a
                                    key={idx}
                                    href={link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-violet-100 text-violet-700 rounded text-xs hover:bg-violet-200 transition-colors"
                                  >
                                    🔗 {link.length > 40 ? link.substring(0, 40) + '...' : link}
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {task.onHoldReason && task.status === 'On Hold' && (
                          <div className="mt-2 p-2 bg-red-50 border border-red-300 rounded-lg">
                            <p className="text-[10px] font-bold text-red-700 mb-0.5 uppercase leading-tight">🛑 On Hold Reason:</p>
                            <p className="text-xs text-red-900 leading-snug">{task.onHoldReason}</p>
                          </div>
                        )}

                        {/* Completion Info - shown when task is Done */}
                        {task.status === 'Done' && (task.completionNote || (task.completionLinks && task.completionLinks.length > 0)) && (
                          <div className="mt-3 p-3 bg-green-50 border-2 border-green-300 rounded-lg">
                            <p className="text-xs font-bold text-green-700 mb-1 uppercase">✅ Completion Details:</p>
                            {task.completionNote && (
                              <p className="text-sm text-green-900 font-medium">{task.completionNote}</p>
                            )}
                            {task.completionLinks && task.completionLinks.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {task.completionLinks.map((link, idx) => (
                                  <a
                                    key={idx}
                                    href={link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs hover:bg-green-200 transition-colors"
                                  >
                                    🔗 {link.length > 40 ? link.substring(0, 40) + '...' : link}
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Extension Info Display */}
                        {task.extensionReason && (
                          <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                            <p className="text-xs font-semibold text-purple-700 mb-1">📅 Due Date Extended</p>
                            <p className="text-sm text-purple-800">{task.extensionReason}</p>
                            {task.originalDueDate && (
                              <p className="text-xs text-purple-600 mt-1">
                                Original due: {format(new Date(task.originalDueDate), 'MMM dd, yyyy')}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Overdue Warning + Extend Button */}
                        {task.isLagging && (
                          <div className="mt-3 p-3 bg-red-50 border border-red-300 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-semibold text-red-700">⚠️ This task is overdue</p>
                                <p className="text-xs text-red-600">
                                  Due: {task.dueDate ? format(new Date(task.dueDate), 'MMM dd, yyyy') : 'Not set'}
                                </p>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openExtensionDialog(task)}
                                className="text-orange-700 border-orange-300 hover:bg-orange-50"
                              >
                                <CalendarPlus className="h-4 w-4 mr-1" />
                                Extend Date
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(task)}
                          className="p-2 hover:bg-gray-200 rounded transition-colors"
                        >
                          <Pencil className="h-4 w-4 text-gray-600" />
                        </button>
                        <button
                          onClick={() => handleDelete(task.id)}
                          className="p-2 hover:bg-red-100 rounded transition-colors"
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </button>
                        {task.status === 'Done' && !task.archived && !task.archivedGlobally && (
                          <button
                            onClick={() => handleArchive(task)}
                            className="p-2 hover:bg-slate-100 rounded transition-colors"
                            data-testid={`archive-task-${task.id}`}
                            title="Archive task"
                          >
                            <Archive className="h-4 w-4 text-slate-600" />
                          </button>
                        )}
                        {(task.archived || task.archivedGlobally) && (
                          <button
                            onClick={() => handleUnarchive(task)}
                            className="p-2 hover:bg-blue-100 rounded transition-colors"
                            data-testid={`unarchive-task-${task.id}`}
                            title="Restore task"
                          >
                            <RotateCcw className="h-4 w-4 text-blue-600" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        ))}
      </div>

      {filteredTasks.length === 0 && (
        <Card className="p-12 text-center bg-white">
          <p className="text-muted-foreground">No tasks for this {weekFilter === 'all' ? 'account' : weekFilter} yet</p>
          <p className="text-sm text-muted-foreground mt-2">Add your first task to get started</p>
        </Card>
      )}

      {/* Extension Dialog */}
      <Dialog open={extensionDialogOpen} onOpenChange={setExtensionDialogOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Extend Due Date</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedTaskForExtension && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="font-semibold text-red-800">{selectedTaskForExtension.title}</p>
                <p className="text-sm text-red-600">
                  Current due date: {selectedTaskForExtension.dueDate ? format(new Date(selectedTaskForExtension.dueDate), 'MMM dd, yyyy') : 'Not set'}
                </p>
              </div>
            )}
            
            <div>
              <Label className="text-sm font-semibold">New Due Date *</Label>
              <Input
                type="date"
                value={extensionData.newDueDate}
                onChange={(e) => setExtensionData({ ...extensionData, newDueDate: e.target.value })}
                className="mt-1.5 bg-white"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div>
              <Label className="text-sm font-semibold">Reason for Extension *</Label>
              <Textarea
                value={extensionData.extensionReason}
                onChange={(e) => setExtensionData({ ...extensionData, extensionReason: e.target.value })}
                rows={3}
                className="mt-1.5 bg-white"
                placeholder="Explain why the due date needs to be extended..."
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setExtensionDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleExtensionSubmit}
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                <CalendarPlus className="h-4 w-4 mr-2" />
                Extend Date
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default AccountDetail;
